/**
 * ManagementColumn — three stacked widgets that originally lived only
 * on the Sales agent's My-Dashboard, now extracted so any "personal"
 * dashboard (marketing user, BDM, etc.) can drop them in with one
 * import.
 *
 *   1. <ManagementFeedback />   - CEO / HOD messages with text + voice
 *                                  replies, filtered by sender role + date.
 *   2. <AIAssistantPanel />      - Free-form chat against /api/agent/chat
 *                                  to log activity and ask questions.
 *   3. <MyDailyReport />         - AI-generated daily report card with
 *                                  manual regenerate.
 *
 * Each widget is self-contained — it owns its own data fetching and
 * state, takes only `token` as a prop, and is safe to mount in
 * isolation. That keeps the host page (MyMarketingDashboard) free of
 * the lifecycle hooks these widgets need.
 *
 * Backend endpoints (unchanged from the sales dashboard implementation):
 *   GET  /api/agent/feedback                 - list feedback addressed to me
 *   POST /api/agent/feedback/{id}/reply      - send text reply
 *   POST /api/agent/feedback/{id}/read       - mark feedback row as read
 *   POST /api/agent/chat                     - send chat message
 *   GET  /api/agent/reports/me               - fetch today's daily report
 *   POST /api/agent/reports/refresh          - (re)generate today's report
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  MessageSquare, FileText, RefreshCw, Send, CheckCircle2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import HelpInfo from './HelpInfo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================================
// 1. Feedback from Management
// ============================================================
export function ManagementFeedback({ token }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filter state — sender role + date range. The unread badge always
  // reflects the FULL feed; filters only narrow what is rendered.
  // Otherwise users would think they'd cleared a feedback they
  // hadn't.
  const [fromRole, setFromRole] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/agent/feedback`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data?.success) {
        const list = Array.isArray(data.data?.feedback) ? data.data.feedback
                   : Array.isArray(data.feedback)       ? data.feedback
                   : [];
        setItems(list);
        setUnread(list.filter((f) => !f.is_read_by_agent).length);
      }
    } catch (e) {
      console.warn('[feedback] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    // Optimistic — flip the local row first, send the request, revert
    // on failure. Keeps the UI snappy on flaky networks.
    setItems((rs) => rs.map((r) => (r.id === id ? { ...r, is_read_by_agent: true } : r)));
    setUnread((c) => Math.max(0, c - 1));
    try {
      await fetch(`${API_URL}/api/agent/feedback/${id}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      // Revert
      setItems((rs) => rs.map((r) => (r.id === id ? { ...r, is_read_by_agent: false } : r)));
      setUnread((c) => c + 1);
    }
  };

  const sendReply = async (id, text) => {
    try {
      const res = await fetch(`${API_URL}/api/agent/feedback/${id}/reply`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reply: text }),
      });
      const data = await res.json();
      if (data?.success) {
        toast.success('Reply sent');
        // Refresh from server so the agent_reply payload lands.
        load();
      } else {
        toast.error(data?.detail || 'Could not send reply');
      }
    } catch (e) {
      toast.error('Network error sending reply');
    }
  };

  // Filter the displayed list. We use millisecond timestamps for the
  // date compare so the inputs (which give YYYY-MM-DD) line up with
  // the API's ISO timestamps.
  const fromMs = from ? new Date(`${from}T00:00:00`).getTime() : null;
  const toMs   = to   ? new Date(`${to}T23:59:59`).getTime()   : null;
  const filtered = items.filter((fb) => {
    const role = (fb.sender_role || '').toLowerCase();
    if (fromRole === 'ceo' && role !== 'ceo') return false;
    if (fromRole === 'hod' && !role.startsWith('hod')) return false;
    const ts = new Date(fb.created_at).getTime();
    if (fromMs && ts < fromMs) return false;
    if (toMs   && ts > toMs)   return false;
    return true;
  });

  const atDefaults = !from && !to && fromRole === 'all';

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquare size={16} className="text-emerald-500" />
            Feedback from Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500">Loading…</p>
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) {
    // Empty state — keep the card visible so the user knows the
    // feature exists rather than hiding silently. Same approach the
    // sales dashboard uses; consistent across roles.
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
            <MessageSquare size={16} className="text-emerald-500" />
            Feedback from Management
            <HelpInfo
              align="inline"
              title="How to use Feedback from Management"
              steps={[
                'Messages from your CEO or HOD land here — text comments and voice notes alike.',
                'Use the filters above the list to narrow by sender role (CEO only / HOD only) or date range.',
                'Click Reply to send a written response that the sender will see in their feedback thread.',
                'Inbound voice notes play directly inside each row — tap the play icon when one arrives.',
                'Hit Mark as Read (or open a reply / expand a long message) to clear the unread count.',
              ]}
              tip="The unread badge counts every new message; filters above only narrow what's rendered, they don't clear the count."
            />
          </CardTitle>
          <CardDescription>Messages from your CEO or HOD appear here.</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 py-6 text-center">
            No feedback yet. Your inbox will show CEO / HOD messages and voice notes.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3 space-y-2">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <MessageSquare size={16} className="text-emerald-500" />
          Feedback from Management
          {unread > 0 && <Badge className="bg-emerald-500 text-white ml-1">{unread} new</Badge>}
          <HelpInfo
            align="inline"
            title="How to use Feedback from Management"
            steps={[
              'Messages from your CEO or HOD land here — text comments and voice notes alike.',
              'Use the filters above the list to narrow by sender role (CEO only / HOD only) or date range.',
              'Click Reply to send a written response that the sender will see in their feedback thread.',
              'Inbound voice notes play directly inside each row — tap the play icon when one arrives.',
              'Hit Mark as Read (or open a reply / expand a long message) to clear the unread count.',
            ]}
            tip="The unread badge counts every new message; filters above only narrow what's rendered, they don't clear the count."
          />
        </CardTitle>
        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <Select value={fromRole} onValueChange={setFromRole}>
            <SelectTrigger className="h-8 w-[120px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All senders</SelectItem>
              <SelectItem value="ceo">CEO only</SelectItem>
              <SelectItem value="hod">HOD only</SelectItem>
            </SelectContent>
          </Select>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-8 px-2 rounded border border-slate-300 text-xs"
            title="From"
          />
          <span className="text-slate-400">to</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-8 px-2 rounded border border-slate-300 text-xs"
            title="To"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={`h-8 text-xs ${atDefaults ? 'text-slate-400 hover:text-rose-600' : 'text-rose-600 hover:text-rose-700'}`}
            onClick={() => { setFrom(''); setTo(''); setFromRole('all'); }}
          >
            Clear
          </Button>
          <span className="ml-auto text-slate-500">
            Showing {filtered.length} of {items.length}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[420px] overflow-y-auto pr-2 space-y-3">
          {filtered.length === 0 && (
            <p className="text-sm py-6 text-center text-slate-500">
              No feedback matches these filters.
            </p>
          )}
          {filtered.map((fb) => (
            <FeedbackRow
              key={fb.id}
              feedback={fb}
              onMarkRead={markRead}
              onReply={sendReply}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Single feedback row with text-reply textarea. Voice reply is omitted
// from this extracted variant — the sales dashboard's voice-reply UI
// depends on AuthContext + the live mic recorder and isn't worth
// duplicating until a marketing user asks for it. The voice PLAY of
// inbound voice notes is preserved (just an <audio> tag).
function FeedbackRow({ feedback, onMarkRead, onReply }) {
  const [replyText, setReplyText] = useState('');
  const [showReply, setShowReply] = useState(false);
  const [sending, setSending] = useState(false);
  // Long messages collapse to a single line until the user expands.
  // Keeps the scrolling list scannable at a glance.
  const [expanded, setExpanded] = useState(false);
  const TRUNCATE_AT = 200;
  const longText = (feedback.feedback_text || '').length > TRUNCATE_AT;
  const visibleText = longText && !expanded
    ? feedback.feedback_text.slice(0, TRUNCATE_AT).trimEnd() + '…'
    : feedback.feedback_text;

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    await onReply(feedback.id, replyText);
    setReplyText('');
    setShowReply(false);
    setSending(false);
    if (!feedback.is_read_by_agent) onMarkRead(feedback.id);
  };

  // Mark read on any active engagement (expand text, play audio,
  // open reply). The unread dot stays until then so the user can
  // scroll and still see what's new.
  const markIfNew = () => {
    if (!feedback.is_read_by_agent) onMarkRead(feedback.id);
  };

  return (
    <div className={`p-3 rounded-lg border ${
      !feedback.is_read_by_agent
        ? 'bg-emerald-50 border-emerald-300'
        : 'bg-slate-50 border-slate-200'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium flex items-center gap-1.5 text-emerald-600">
            {!feedback.is_read_by_agent && (
              <span
                className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse"
                title="New, unread"
              />
            )}
            From: {feedback.sender_name} ({feedback.sender_role?.replace(/_/g, ' ')})
          </p>
          {feedback.feedback_text && (
            <p className="text-sm mt-1 whitespace-pre-wrap text-slate-900">
              {visibleText}
              {longText && (
                <button
                  type="button"
                  onClick={() => { setExpanded((v) => !v); markIfNew(); }}
                  className="ml-1 text-xs font-medium underline text-emerald-600"
                >
                  {expanded ? 'See less' : 'See more'}
                </button>
              )}
            </p>
          )}
          {feedback.voice_message_id && (
            <div className="mt-2 rounded-md p-2 bg-white border border-slate-200">
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                <span>🎙</span>
                <span>Voice note · {Math.round(feedback.voice_duration_seconds || 0)}s</span>
              </div>
              <audio
                controls
                preload="none"
                src={`${API_URL}/api/voice-messages/${feedback.voice_message_id}/audio`}
                className="w-full"
                onPlay={markIfNew}
              />
            </div>
          )}
          <p className="text-xs mt-1 text-slate-500">
            {new Date(feedback.created_at).toLocaleString()}
          </p>
        </div>
        {!feedback.is_read_by_agent && (
          <Badge className="bg-emerald-500 text-white text-xs shrink-0">New</Badge>
        )}
      </div>

      {/* Reply zone — show existing reply OR the compose form. */}
      {(feedback.agent_reply || feedback.agent_voice_reply_id) ? (
        <div className="mt-3 pl-3 border-l-2 border-violet-400">
          <p className="text-xs font-medium text-violet-600">Your reply:</p>
          {feedback.agent_reply && (
            <p className="text-sm text-slate-700">{feedback.agent_reply}</p>
          )}
          {feedback.agent_voice_reply_id && (
            <audio
              controls
              preload="none"
              src={`${API_URL}/api/voice-messages/${feedback.agent_voice_reply_id}/audio`}
              className="w-full mt-1"
            />
          )}
          <p className="text-xs mt-1 text-slate-500">
            {feedback.agent_replied_at && new Date(feedback.agent_replied_at).toLocaleString()}
          </p>
        </div>
      ) : (
        <div className="mt-3">
          {showReply ? (
            <div className="space-y-2">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write your reply..."
                rows={2}
                className="w-full px-3 py-2 text-sm rounded-md border border-slate-200 bg-white text-slate-900"
                autoFocus
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSendReply} disabled={sending || !replyText.trim()}>
                  Send
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setShowReply(false); setReplyText(''); }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => { setShowReply(true); markIfNew(); }}
            >
              <MessageSquare size={12} className="mr-1.5" />
              Reply
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// 2. AI Assistant Panel
// ============================================================
export function AIAssistantPanel({ token }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollerRef = useRef(null);

  // Keep the latest message in view as the conversation grows.
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    const text = input;
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/agent/chat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        // Strip extra fields from history — the backend's Pydantic
        // model only allows string values, so booleans like
        // activity_logged from previous assistant bubbles cause a 422.
        body: JSON.stringify({
          message: text,
          conversation_history: messages.slice(-10).map((m) => ({
            role: String(m.role || ''),
            content: String(m.content || ''),
          })),
        }),
      });
      let data = null;
      try { data = await response.json(); } catch { /* not json */ }

      if (response.ok && data?.success) {
        const reply = data?.data?.reply || data?.reply || '(empty reply)';
        setMessages((prev) => [...prev, {
          role: 'assistant',
          content: reply,
          activity_logged: data?.data?.activity_logged,
          report_updated: data?.data?.report_updated,
        }]);
      } else {
        const msg = data?.detail || data?.error || `Chat failed (HTTP ${response.status})`;
        toast.error(msg);
        setMessages((prev) => [...prev, {
          role: 'assistant',
          content: `⚠️ ${msg}`,
          error: true,
        }]);
      }
    } catch (error) {
      toast.error(`Network error: ${error.message || error}`);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `⚠️ Network error: ${error.message || error}`,
        error: true,
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="flex flex-col h-[500px]">
      <CardHeader className="pb-3 border-b">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <MessageSquare size={16} />
          AI Assistant
          <HelpInfo
            align="inline"
            title="How to use the AI Assistant"
            steps={[
              'Type natural-language updates like "Met Mr. Khan today, he wants 3BR Marina view" — the AI parses and logs the activity to the right lead automatically.',
              'Ask analysis questions: "Which campaigns are underperforming?" or "Summarise my pipeline" — answers pull from your live data.',
              'Use it to draft messages: "Write a follow-up email to Sarah about Skyline 459".',
              <>Activities it logs land in your <em>Activity Logs</em> and update today&apos;s Daily Report below — your HOD sees the same record.</>,
            ]}
            tip="Shorter, clearer messages parse better. One activity per message works best — split multi-topic updates into separate lines."
          />
        </CardTitle>
        <CardDescription>Report activities and get insights</CardDescription>
      </CardHeader>
      <div ref={scrollerRef} className="flex-1 p-4 overflow-y-auto">
        <div className="space-y-3">
          {messages.length === 0 && (
            <p className="text-sm text-center text-slate-500 py-8">
              Tell me what you've been working on today...
            </p>
          )}
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] rounded-lg px-3 py-2 ${
                msg.role === 'user'
                  ? 'bg-violet-600 text-white'
                  : msg.error
                    ? 'bg-red-50 text-red-700 border border-red-200'
                    : 'bg-slate-100 text-slate-900'
              }`}>
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                {msg.activity_logged && (
                  <Badge variant="outline" className="mt-2 text-xs">Activity logged</Badge>
                )}
                {msg.report_updated && (
                  <Badge variant="outline" className="mt-2 ml-1 text-xs">Report updated</Badge>
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="rounded-lg px-3 py-2 bg-slate-100">
                <RefreshCw size={14} className="animate-spin text-slate-500" />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="p-4 border-t">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder="Type your update..."
            className="flex-1 px-3 py-2 rounded-md border border-slate-200 bg-white text-sm"
            disabled={isLoading}
          />
          <Button onClick={sendMessage} disabled={isLoading || !input.trim()}>
            <Send size={14} />
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ============================================================
// 3. My Daily Report
// ============================================================
export function MyDailyReport({ token }) {
  const [report, setReport]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefresh]  = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/agent/reports/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data?.success) {
        setReport(data.data?.report || null);
      }
    } catch (e) {
      console.warn('[my-report] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    setRefresh(true);
    try {
      const res = await fetch(`${API_URL}/api/agent/reports/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data?.success) {
        setReport(data.data?.report || null);
        toast.success('Report refreshed');
      } else {
        toast.error(data?.detail || 'Could not refresh report');
      }
    } catch (e) {
      toast.error('Network error refreshing report');
    } finally {
      setRefresh(false);
    }
  };

  const Header = ({ children }) => (
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2 flex-wrap">
        <FileText size={16} />
        My Daily Report
        <HelpInfo
          align="inline"
          title="How My Daily Report works"
          steps={[
            'The AI summarises your day automatically based on your logged activities, calls, meetings, and lead updates — you don\'t write the report yourself.',
            'Click Generate Report to produce today\'s summary. The AI compiles what you\'ve done so far and structures it into sections.',
            <>The <strong>Seen by HOD</strong> badge appears once your HOD opens the report. If it&apos;s missing at end of day, your HOD hasn&apos;t seen it yet.</>,
            'Click Refresh later to regenerate after logging more activity — useful right before leaving for the day.',
            'Sections: Daily Summary, Pipeline Progress, Lead Engagement, Follow-up Status, Flags & Attention.',
          ]}
          tip="Log activities throughout the day, not all at end-of-day — the AI captures more detail when entries are fresh."
        />
        {children}
      </CardTitle>
    </CardHeader>
  );

  if (loading) {
    return (
      <Card>
        <Header />
        <CardContent>
          <p className="text-xs text-slate-500">Loading…</p>
        </CardContent>
      </Card>
    );
  }

  if (!report) {
    return (
      <Card>
        <Header />
        <CardContent className="p-6 text-center">
          <FileText size={40} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm text-slate-500">No report generated yet for today</p>
          <Button onClick={regenerate} disabled={refreshing} className="mt-4 gap-2">
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            Generate Report
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <Header>
        {report.hod_viewed && (
          <Badge variant="outline" className="text-xs text-emerald-600 ml-auto">
            <CheckCircle2 size={11} className="mr-1" />
            Seen by HOD
          </Badge>
        )}
        {report.version != null && (
          <Badge variant="secondary" className="text-xs">v{report.version}</Badge>
        )}
      </Header>
      <CardContent>
        <p className="text-xs text-slate-500 mb-3">
          Last updated: {report.generated_at ? new Date(report.generated_at).toLocaleString() : '—'}
          {report.trigger_type && (
            <span className="ml-2 px-2 py-0.5 rounded text-xs bg-slate-100">
              {String(report.trigger_type).replace(/_/g, ' ')}
            </span>
          )}
        </p>
        <div className="h-[300px] overflow-y-auto pr-2">
          <div
            className="prose prose-sm max-w-none"
            // The backend returns the report as markdown-ish text. We
            // do a minimal substitution so headings + bullets render
            // — keeps the rest as plain paragraphs.
            dangerouslySetInnerHTML={{
              __html: (report.report_text || '')
                .replace(/\n/g, '<br/>')
                .replace(/##\s*/g, '<h4 style="margin-top:12px;font-weight:600;font-size:13px;color:#1e293b">')
                .replace(/-\s/g, '• '),
            }}
          />
        </div>
        <div className="mt-3 pt-3 border-t flex items-center justify-between gap-2">
          <p className="text-[11px] text-slate-500">
            Updates automatically as you log activity.
          </p>
          <Button onClick={regenerate} variant="outline" size="sm" disabled={refreshing}>
            <RefreshCw size={12} className={`mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Convenience export — drop all three into a column with one element.
// Marketing dashboards use this; other pages can mount the individual
// widgets if they want a different layout.
// ============================================================
export default function ManagementColumn({ token }) {
  return (
    <div className="space-y-4">
      <ManagementFeedback token={token} />
      <AIAssistantPanel token={token} />
      <MyDailyReport token={token} />
    </div>
  );
}
